import type {
  RecordDetailsForm,
  Service,
} from '@app/components/RecordModal/components/RecordDetails/types';
import { getServiceDiscountsSum } from '@app/components/RecordModal/components/RecordDetails/utils';
import type {
  ClientServiceInterface,
  ClientServicesInterface,
  ClientServicesRequestInterface,
} from '@app/types';

/**
 * ID абонемента клиента у услуги записи. GetRecordById отдаёт у обычной услуги
 * ClientSubscriptionId = 0 вместо null (beauty-crm LogicRecords.cs:2525-2571).
 * UpdateRecord считает услугу с абонемента по ClientSubscriptionId != null и
 * обнуляет её цену (LogicRecords.cs:1669-1672, :4650-4658), поэтому 0 — это
 * «нет абонемента».
 */
export const normalizeClientSubscriptionId = (
  clientSubscriptionId: number | null | undefined
): number | null => clientSubscriptionId || null;

/** Пустая строка «Услуг» окна записи */
export const createEmptyService = (): Service => ({
  discountCurrency: 0,
  discountPercent: 0,
  price: 0,
  quantity: 0,
  service: null,
  upsaleEmployee: null,
  duration: 0,
  isExcludedFromLoyaltyDiscount: false,
});

/** Строка «Услуг» окна записи из услуги ответа GetRecordById */
export const mapRecordServiceToFormService = ({
  Id,
  Name,
  CategoryId,
  Category,
  PriceS,
  Duration,
  Count,
  UserId,
  Discount,
  Discounts,
  Paid,
  IsExcludedFromLoyaltyDiscount,
  ClientSubscriptionId,
}: ClientServicesInterface) => {
  const clientSubscriptionId =
    normalizeClientSubscriptionId(ClientSubscriptionId);
  const isFromSubscription = clientSubscriptionId !== null;
  const total = PriceS * Count;

  return {
    service: Id?.toString(),
    name: Name || '',
    categoryId: CategoryId || 0,
    category: Category || '',
    price: PriceS || 0,
    duration: Duration,
    quantity: Count,
    upsaleEmployee: UserId || null,
    discountCurrency: Discount,
    // У услуги с абонемента цена 0: процент скидки — 0, а не NaN.
    discountPercent: total > 0 ? (100 * Discount) / total : 0,
    totalDiscount: Discounts?.length
      ? getServiceDiscountsSum(Discounts)
      : Discount,
    paid: Paid,
    // Услуга с абонемента всегда исключена из скидки лояльности — так её
    // отдаёт GetClientSubscriptions (beauty-crm LogicSubscriptions.cs:639).
    isExcludedFromLoyaltyDiscount:
      isFromSubscription || IsExcludedFromLoyaltyDiscount,
    clientSubscriptionId,
  };
};

/** Строки «Услуг» окна записи из ответа GetRecordById */
export const mapRecordServicesToFormServices = (
  recordServices: ClientServicesInterface[] | null | undefined
): Service[] => (recordServices || []).map(mapRecordServiceToFormService);

/**
 * Услуги записи для UpdateRecord. Данные услуги берутся из услуг исполнителя:
 * услуга, которой нет у исполнителя, остаётся без Id и в запрос не уходит.
 * У услуги с абонемента цена и скидка — 0, она исключена из скидки
 * лояльности: так её считает бэкенд (LogicRecords.cs:4650-4658).
 */
export const buildRecordClientServices = (
  services: Service[],
  clientServices: ClientServiceInterface[]
): ClientServicesRequestInterface[] =>
  services
    .map((service) => {
      const data = clientServices.find(
        ({ Id }) => Id === +(service?.service ?? 0)
      );
      const {
        discountCurrency,
        duration,
        price,
        quantity,
        upsaleEmployee,
        isExcludedFromLoyaltyDiscount,
      } = service;
      const clientSubscriptionId = normalizeClientSubscriptionId(
        service.clientSubscriptionId
      );
      const isFromSubscription = clientSubscriptionId !== null;

      return {
        ...data,
        Discount: isFromSubscription ? 0 : discountCurrency,
        Count: quantity,
        Duration: duration,
        PriceS: isFromSubscription ? 0 : price,
        UserId: upsaleEmployee,
        IsExcludedFromLoyaltyDiscount:
          isFromSubscription || isExcludedFromLoyaltyDiscount,
        ClientSubscriptionId: clientSubscriptionId,
      };
    })
    .filter((service) => !!service && service.Id);

/**
 * Строки «Услуг» без услуг с абонемента — при смене клиента записи. Абонемент
 * принадлежит прежнему клиенту, а бэкенд владельца абонемента не проверяет
 * (beauty-crm LogicRecords.cs:2109-2115). Обычные услуги остаются. null —
 * убирать нечего.
 */
export const removeClientSubscriptionServices = (
  services: Service[]
): Service[] | null => {
  const restServices = services.filter(
    (service) =>
      normalizeClientSubscriptionId(service?.clientSubscriptionId) === null
  );

  if (restServices.length === services.length) return null;

  return restServices.some((service) => service?.service)
    ? restServices
    : [createEmptyService()];
};

/**
 * Услуги с абонемента, которых нет у исполнителя записи: после смены
 * исполнителя buildRecordClientServices не нашёл бы их данных и молча убрал
 * из запроса, а бэкенд снял бы их с записи (LogicRecords.cs:1698-1704).
 */
export const getNotProvidedSubscriptionServices = (
  services: Service[],
  clientServices: ClientServiceInterface[]
): Service[] =>
  services.filter(
    (service) =>
      Boolean(service?.service) &&
      normalizeClientSubscriptionId(service.clientSubscriptionId) !== null &&
      !clientServices.some(({ Id }) => Id === +(service.service ?? 0))
  );

const SERVICE_WITHOUT_NAME = 'Услуга без названия';

/** Названия строк «Услуг» для уведомления */
export const getServiceNames = (services: Service[]): string =>
  services.map(({ name }) => name || SERVICE_WITHOUT_NAME).join(', ');

/**
 * Услуги сохранённой записи, которые нельзя сохранить в новом виде.
 * UpdateRecord находит услугу записи по Id (LogicRecords.cs:1645-1647) и
 * смену ClientSubscriptionId (платная ↔ с абонемента, другой абонемент) не
 * видит: цена станет 0, а единица абонемента не займётся
 * (LogicRecords.cs:1661-1672, :2106-2141), или единицы останутся занятыми у
 * платной услуги (:2186-2192 не выполняется). Смену количества услуги с
 * абонемента UpdateRecord переносит на единицы абонемента
 * (LogicRecords.cs:1674-1679), её не запрещаем.
 * Название берётся из сохранённой строки: Id тот же, а название строки
 * формы могло остаться от другой услуги.
 */
export const getChangedSavedServices = (
  services: Service[],
  savedServices: Service[]
): Service[] => {
  const savedById = new Map(
    savedServices
      .filter((saved) => saved?.service)
      .map((saved) => [saved.service, saved])
  );

  return services
    .filter((service) => {
      if (!service?.service) return false;

      const saved = savedById.get(service.service);

      if (!saved) return false;

      return (
        normalizeClientSubscriptionId(saved.clientSubscriptionId) !==
        normalizeClientSubscriptionId(service.clientSubscriptionId)
      );
    })
    .map((service) => ({
      ...service,
      name: savedById.get(service.service)?.name || service.name,
    }));
};

/** Уведомление о сохранённых услугах, которые нельзя изменить */
export const getChangedSavedServicesMessage = (services: Service[]): string =>
  services.length === 1
    ? `Нельзя изменить услугу ${getServiceNames(
        services
      )}: она уже сохранена в записи. Сохраните запись без этой услуги, потом добавьте её снова`
    : `Нельзя изменить услуги ${getServiceNames(
        services
      )}: они уже сохранены в записи. Сохраните запись без этих услуг, потом добавьте их снова`;

/** Сохранённые данные окна записи с новыми строками «Услуг» */
export const replaceSavedServices = <
  SavedData extends Pick<RecordDetailsForm, 'services'>
>(
  savedData: SavedData | null,
  services: Service[]
): SavedData | null => savedData && { ...savedData, services };

interface UpdateRecordWithSavedServicesProps<Response> {
  /** UpdateRecord с unwrap(): ошибка бэкенда — отклонённый промис */
  updateRecord(): Promise<Response>;
  /** Строки «Услуг», которые уходят в UpdateRecord */
  services: Service[];
  /** Новая опора предела количества услуг с абонемента */
  onServicesSaved(services: Service[]): void;
}

/**
 * UpdateRecord, потом новая опора предела (task/11536). UpdateRecord занимает
 * и освобождает единицы абонемента по количеству (LogicRecords.cs:1674-1679):
 * опора меняется только после успешного ответа. При ошибке бэкенда опора
 * остаётся прежней, а ошибка уходит дальше.
 */
export const updateRecordWithSavedServices = async <Response>({
  updateRecord,
  services,
  onServicesSaved,
}: UpdateRecordWithSavedServicesProps<Response>): Promise<Response> => {
  const response = await updateRecord();

  onServicesSaved(services);

  return response;
};
